import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, CircleNotch } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  { key: "name", label: "Project / Work Name", placeholder: "e.g. Construction of Link Road Udhampur", required: true },
  { key: "client", label: "Client / Department", placeholder: "e.g. PWD (R&B) Division Udhampur" },
  { key: "location", label: "Location", placeholder: "e.g. Udhampur, J&K" },
  { key: "work_order_no", label: "Tender / Work Order No.", placeholder: "Optional" },
];

export function NewProjectDialog() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", location: "", work_order_no: "", default_deduction: "", gst_rate: 18 });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Project name is required");
    const below = Number(form.default_deduction);
    if (form.default_deduction === "" || Number.isNaN(below) || below < 0 || below > 100) return toast.error("Enter your tender % below Govt rate (0–100)");
    setBusy(true);
    try {
      const { data } = await api.post("/projects", {
        ...form, default_deduction: below, gst_rate: Number(form.gst_rate) || 0,
      });
      toast.success("Project created");
      navigate(`/projects/${data.project_id}`);
    } catch (err) {
      toast.error(errMsg(err, "Could not create project"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-project-button" className="inline-flex items-center gap-2 h-10 px-4 bg-emerald-600 text-white text-sm font-medium rounded-sm hover:bg-emerald-700 transition-colors shadow-sm">
          <Plus size={16} weight="bold" /> New Project
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="new-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">New BOQ Project</DialogTitle>
          <DialogDescription>Create a project, then upload the Government BOQ file.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={f.key}>{f.label}{f.required && <span className="text-rose-600"> *</span>}</Label>
              <Input id={f.key} data-testid={`new-project-${f.key}-input`} value={form[f.key]} onChange={set(f.key)} placeholder={f.placeholder} className="rounded-sm" />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ded">Tender % Below Govt Rate<span className="text-rose-600"> *</span></Label>
              <Input id="ded" type="number" step="0.01" min="0" max="100" placeholder="e.g. 25" data-testid="new-project-deduction-input" value={form.default_deduction} onChange={set("default_deduction")} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gst">GST %</Label>
              <Input id="gst" type="number" step="0.01" data-testid="new-project-gst-input" value={form.gst_rate} onChange={set("gst_rate")} className="rounded-sm" />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="new-project-submit-button" className="w-full h-11 inline-flex items-center justify-center gap-2 bg-slate-900 text-white font-medium rounded-sm hover:bg-slate-800 transition-colors disabled:opacity-60">
            {busy && <CircleNotch className="animate-spin" size={16} />} Create Project
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
